import { UserManager, UserManagerSettings, User } from "oidc-client-ts";
import {
  useEffect,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { nuclearn_user_token } from "src/App";
import { setPostHogUser } from "src/config/posthog";
import {
  useGetProviders,
  OAuthProviderAPI,
} from "src/views/oauth/OAuthServices";
import { sha256 } from "js-sha256";

if (!window.ENV?.NUCLEARN_API_URL) {
  throw new Error("NUCLEARN_API_URL is not defined");
}
let nuclearn_api_url: string = window.ENV?.NUCLEARN_API_URL;

// Extend the UserManagerSettings interface
interface ExtendedUserManagerSettings extends UserManagerSettings {
  useCodeVerifier?: boolean;
}
// Define supported OAuth providers
export type OAuthProvider = {
  id: number;
  name: string;
  icon?: React.ReactNode;
  config: ExtendedUserManagerSettings;
  pkceManager?: PKCEManager;
  // OIDC Core §5.5 claims request JSON. Pre-built by the backend
  // when the provider's ``request_claims_explicitly`` toggle is on
  // and there's something to request; null otherwise. When
  // non-null, passed through as ``claims=<json>`` on the /authorize
  // redirect.
  claims_request?: string | null;
};

class PKCEManager {
  private static instance: PKCEManager;
  private codeVerifier: string | null = null;
  private codeChallenge: string | null = null;

  private constructor() {}

  public static getInstance(): PKCEManager {
    if (!PKCEManager.instance) {
      PKCEManager.instance = new PKCEManager();
    }
    return PKCEManager.instance;
  }

  // Generate a random code verifier
  private generateCodeVerifier(length = 64): string {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    let verifier = "";
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    array.forEach((value) => {
      verifier += charset[value % charset.length];
    });
    return verifier;
  }

  // Base64URL encode an array buffer
  private base64URLEncode(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  // Generate code challenge from verifier
  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hashBuffer = await this.sha256Sync(data);
    return this.base64URLEncode(hashBuffer);
  }

  private async sha256Sync(data: any) {
    try {
      if (
        typeof window !== "undefined" &&
        window.crypto &&
        window.crypto.subtle
      ) {
        try {
          const digest = await window.crypto.subtle.digest("SHA-256", data);
          return digest;
        } catch (webCryptoError) {
          console.warn("Web Crypto API failed, falling back:", webCryptoError);
        }
      }

      // Fallback using js-sha256
      return this.sha256Fallback(data);
    } catch (error) {
      console.error("Error in sha256Sync:", error);
      return this.sha256Fallback(data);
    }
  }

  private sha256Fallback(data: any) {
    console.log("Using SHA-256 fallback implementation with js-sha256");
    const digest = sha256.arrayBuffer(data);
    return digest;
  }

  // Initialize PKCE values if they don't exist
  public async initialize(recreate = false): Promise<void> {
    // Check if values are already in storage
    const storedVerifier = sessionStorage.getItem("pkce_verifier");
    const storedChallenge = sessionStorage.getItem("pkce_challenge");

    if (storedVerifier && storedChallenge && !recreate) {
      this.codeVerifier = storedVerifier;
      this.codeChallenge = storedChallenge;
      return;
    }

    // Generate new values if not found
    this.codeVerifier = this.generateCodeVerifier();
    this.codeChallenge = await this.generateCodeChallenge(this.codeVerifier);
    // remove the old values
    sessionStorage.removeItem("pkce_verifier");
    sessionStorage.removeItem("pkce_challenge");
    // Store the values
    sessionStorage.setItem("pkce_verifier", this.codeVerifier);
    sessionStorage.setItem("pkce_challenge", this.codeChallenge);
  }

  // Get the code verifier
  public getCodeVerifier(): string {
    if (!this.codeVerifier) {
      throw new Error("PKCE not initialized. Call initialize() first.");
    }
    return this.codeVerifier;
  }

  // Get the code challenge
  public getCodeChallenge(): string {
    if (!this.codeChallenge) {
      throw new Error("PKCE not initialized. Call initialize() first.");
    }
    return this.codeChallenge;
  }

  // Clear stored values (useful for logout)
  public clear(): void {
    this.codeVerifier = null;
    this.codeChallenge = null;
    sessionStorage.removeItem("pkce_verifier");
    sessionStorage.removeItem("pkce_challenge");
  }
}

// OAuth Service class
export class OAuthService {
  private static instances: Map<number, OAuthService> = new Map();
  private manager: UserManager;
  private provider: OAuthProvider;
  private hasCalledCallback: boolean = false;

  private constructor(provider: OAuthProvider) {
    this.provider = provider;
    this.manager = new UserManager(provider?.config);
    this.initializePKCEManager();
  }

  private async initializePKCEManager(): Promise<void> {
    if (!this.provider.pkceManager) {
      this.provider.pkceManager = PKCEManager.getInstance();
      await this.provider.pkceManager.initialize();
    }
  }

  public static getInstance(provider: OAuthProvider): OAuthService {
    if (!OAuthService.instances.has(provider.id)) {
      OAuthService.instances.set(provider.id, new OAuthService(provider));
    }
    return OAuthService.instances.get(provider.id)!;
  }

  public static async reinitializePKCEManager(
    provider: OAuthProvider,
  ): Promise<OAuthService> {
    if (provider.pkceManager) {
      provider.pkceManager.clear();
    }
    provider.pkceManager = PKCEManager.getInstance();
    await provider.pkceManager.initialize(true);
    const instance = OAuthService.getInstance(provider);
    // Reset callback flag for new login attempt
    instance.hasCalledCallback = false;
    return instance;
  }

  public async login(): Promise<void> {
    try {
      // Reset callback flag for new login attempt
      this.hasCalledCallback = false;
      sessionStorage.setItem("oauth_provider_id", String(this.provider.id));
      sessionStorage.setItem("return_to", window.location.pathname);
      let codeChallenge = sessionStorage.getItem("pkce_challenge");
      if (!codeChallenge) {
        console.error("PKCE challenge not found.");
        return;
      }

      // Generate nonce for replay attack prevention (validated by backend)
      // Note: State/CSRF protection is handled by oidc-client-ts library internally
      const nonce = crypto.randomUUID();
      sessionStorage.setItem("oauth_nonce", nonce);

      // Pass the optional OIDC §5.5 claims request parameter when the
      // backend has built one for this provider. Voluntary requests
      // only — the spec (§5.5.1) forbids the IdP from failing the
      // login over a voluntary claim it can't release, so this is
      // safe to send even before GE has provisioned all attributes.
      const extraQueryParams: Record<string, string> = {
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
      };
      if (this.provider.claims_request) {
        extraQueryParams.claims = this.provider.claims_request;
      }

      await this.manager.signinRedirect({
        nonce: nonce,
        extraQueryParams,
      });
    } catch (error) {
      console.error(`${this.provider.name} login failed:`, error);
      throw error;
    }
  }

  private async getUserFromToken(): Promise<any> {
    try {
      const response = await axios.get(`${nuclearn_api_url}/user/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  }

  private getErrorMessage(error: string, description: string): string {
    // Map technical OAuth errors to user-friendly messages
    const errorMessages: Record<string, string> = {
      access_denied:
        "Authentication was cancelled or denied. Please try again.",
      invalid_request:
        "Authentication request was invalid. Please contact support.",
      unauthorized_client:
        "This application is not authorized. Please contact your administrator.",
      unsupported_response_type:
        "Authentication method not supported. Please contact support.",
      invalid_scope:
        "Requested permissions are not allowed. Please contact your administrator.",
      server_error:
        "The identity provider encountered an error. Please try again later.",
      temporarily_unavailable:
        "The identity provider is temporarily unavailable. Please try again later.",
    };

    return (
      errorMessages[error] ||
      description ||
      "Authentication failed. Please try again."
    );
  }

  private cleanupOAuthState(): void {
    sessionStorage.removeItem("oauth_provider_id");
    sessionStorage.removeItem("return_to");
    sessionStorage.removeItem("pkce_verifier");
    sessionStorage.removeItem("pkce_challenge");
    sessionStorage.removeItem("oauth_nonce");
  }

  public async handleCallback(history: any): Promise<User | undefined> {
    if (this.hasCalledCallback) {
      return;
    }

    // Get fresh URL params (the global url_params is stale from module load)
    const url_params = new URLSearchParams(window.location.search);

    // Only process if this looks like an OAuth callback (has code or error)
    const code = url_params.get("code");
    const error = url_params.get("error");

    if (!code && !error) {
      // Not an OAuth callback - just return without processing
      // Don't clean up state here as a login redirect may be pending
      return;
    }

    this.hasCalledCallback = true;

    try {
      // Check for OAuth error FIRST
      if (error) {
        const errorDescription =
          url_params.get("error_description") || "Authentication failed";
        const errorUri = url_params.get("error_uri");
        console.error(`OAuth error: ${error} - ${errorDescription}`, errorUri);

        // Clean up OAuth state
        this.cleanupOAuthState();

        // Redirect to login with user-friendly error message
        history.replace(
          `/login?error=${encodeURIComponent(
            this.getErrorMessage(error, errorDescription),
          )}`,
        );
        return;
      }

      // Note: State validation is handled by oidc-client-ts library internally.
      // The library generates its own state parameter and validates it on callback.
      // We don't do manual state validation here as it conflicts with the library.

      // Include nonce in the request for backend validation
      const nonce = sessionStorage.getItem("oauth_nonce");
      // Exchange material (code, PKCE verifier) goes in the POST body,
      // never the URL — query strings land in proxy/server access logs
      // (CWE-598). ``redirect_uri`` must match the one used in the
      // authorize request (host-dependent
      // ``${window.location.origin}/login``); when it doesn't (e.g. the
      // app is reached via public .dev vs internal) the IdP rejects the
      // exchange with invalid_grant. Consumes the backend's optional
      // redirect_uri override (MRE #14606).
      const exchangePayload: Record<string, string> = {
        provider: this.provider.name,
        code: code ?? "",
        code_verifier: sessionStorage.getItem("pkce_verifier") ?? "",
        redirect_uri: `${window.location.origin}/login`,
      };
      if (nonce) {
        exchangePayload.nonce = nonce;
      }

      let res = await fetch(`${nuclearn_api_url}/auth/login-oauth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(exchangePayload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const detail =
          typeof errorData?.detail === "string"
            ? errorData.detail
            : errorData?.detail?.message ||
              "Authentication failed. Please try again.";
        this.cleanupOAuthState();
        history.replace(`/login?error=${encodeURIComponent(detail)}`);
        return;
      }

      let user_data: any;
      try {
        user_data = await res.json();
      } catch {
        this.cleanupOAuthState();
        history.replace(
          `/login?error=${encodeURIComponent("Invalid response from server. Please try again.")}`,
        );
        return;
      }

      if (!user_data?.access_token) {
        this.cleanupOAuthState();
        history.replace(
          `/login?error=${encodeURIComponent("Authentication failed: no access token received. Please try again.")}`,
        );
        return;
      }

      // Store the token
      localStorage.setItem(
        nuclearn_user_token as string,
        `${user_data.token_type} ${user_data.access_token}`,
      );

      // Get user data
      const userData = await this.getUserFromToken();
      localStorage.setItem("nuclearn_user", JSON.stringify(userData));
      setPostHogUser();

      // Clean up and redirect
      this.cleanupOAuthState();
      history.replace("/");
    } catch (error) {
      console.error("Error in handleCallback:", error);
      this.cleanupOAuthState();
      history.replace(
        `/login?error=${encodeURIComponent("An unexpected error occurred. Please try again.")}`,
      );
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.manager.removeUser();
      localStorage.removeItem(nuclearn_user_token as string);
      localStorage.removeItem("nuclearn_user");
      sessionStorage.removeItem("oauth_provider_id");
      this.hasCalledCallback = false;

      // OIDC RP-Initiated Logout 1.0: when the IdP exposes an
      // end_session_endpoint, redirect through it so the user's IdP
      // session is also terminated. Otherwise the user stays signed
      // in at the IdP and the next login auto-re-auths from the
      // IdP's cookie. ``signoutRedirect`` writes the redirect itself,
      // so we don't manually set window.location.href when it runs.
      const endSession = (this.provider?.config?.metadata as any)
        ?.end_session_endpoint;
      if (endSession) {
        try {
          await this.manager.signoutRedirect();
          return;
        } catch (err) {
          // If RP-initiated logout fails (misconfigured IdP, redirect
          // URI not registered, etc.), fall back to local-only.
          console.warn(
            "OIDC end-session redirect failed; falling back to local logout:",
            err,
          );
        }
      }

      window.location.href = "/";
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  }

  public getUser(): Promise<User | null> {
    return this.manager.getUser();
  }

  public async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!user && !user.expired;
  }
}

// React Context
interface OAuthContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  login: (providerId: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: () => Promise<boolean>;
}

const OAuthContext = createContext<OAuthContextType | null>(null);

// Build the oidc-client-ts UserManager config from a raw API provider.
// Single source of truth: EVERY OAuthService instance (login button,
// callback handler, and the context) must be created from this so it
// carries ``disablePKCE: true`` + the MRE ``token_endpoint`` override.
// A raw API provider has no ``disablePKCE`` field, so oidc-client-ts
// defaults it to false and appends a SECOND code_challenge on top of the
// manual one — which Okta rejects as a duplicate parameter.
export function buildOAuthProvider(provider: OAuthProviderAPI): OAuthProvider {
  return {
    id: provider.id,
    name: provider.name,
    claims_request: provider.claims_request ?? null,
    config: {
      // ``authority`` is a required string in UserManagerSettings; the
      // metadata map is string|undefined, so fall back to "" (a provider
      // whose discovery lacks an issuer can't complete a flow anyway).
      authority: provider.config.metadata.issuer ?? "",
      client_id: provider.config.client_id,
      redirect_uri: `${window.location.origin}/login`,
      // ``signoutRedirect`` lands here after the IdP completes its
      // end-session flow. Same path as login so the unauthenticated
      // page renders cleanly.
      post_logout_redirect_uri: `${window.location.origin}/login`,
      response_type: "code",
      scope: provider.config.scope,
      loadUserInfo: false,
      // Load-bearing: stops oidc-client-ts from appending its OWN
      // code_challenge/code_challenge_method. We run PKCE manually
      // (PKCEManager → login()'s extraQueryParams), so the library's
      // built-in PKCE would collide with ours. PKCE IS active; this
      // only disables the *library's* duplicate implementation.
      disablePKCE: true,
      metadata: {
        authorization_endpoint:
          provider?.config?.metadata?.authorization_endpoint,
        token_endpoint: `${nuclearn_api_url}/auth/login-oauth?provider=${provider.id}`,
        userinfo_endpoint: `${nuclearn_api_url}/auth/userinfo`,
        // OIDC RP-Initiated Logout 1.0 — when the IdP exposes an
        // end_session_endpoint, the OAuthService.logout method uses
        // ``signoutRedirect`` to send the user through it. Empty string
        // when the IdP doesn't advertise one; logout falls back to
        // local-only.
        end_session_endpoint: provider?.config?.metadata?.end_session_endpoint,
      },
    },
  };
}

// Provider Component
export const OAuthProvider: React.FC<{
  children: React.ReactNode;
  nuclearn_api_url?: string;
}> = ({ children }) => {
  const { data: providerListData } = useGetProviders();
  const [providers, setProviders] = useState<Record<string, OAuthProvider>>({});
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [providerId, setProviderId] = useState<string | null>(() =>
    sessionStorage.getItem("oauth_provider_id"),
  );

  // Initialize providers
  useEffect(() => {
    if (Array.isArray(providerListData)) {
      const newProviders: Record<string, OAuthProvider> = {};
      providerListData.forEach((provider: any) => {
        newProviders[provider.id] = buildOAuthProvider(provider);
      });
      setProviders(newProviders);
    }
  }, [providerListData]);

  // add the pkce manager
  const pkceManager = PKCEManager.getInstance();
  const initializePKCE = async () => {
    // Don't reinitialize PKCE if we're in the middle of an OAuth callback
    // The callback needs to use the existing verifier from sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("code") || urlParams.has("error")) {
      // Just restore from sessionStorage without regenerating
      const storedVerifier = sessionStorage.getItem("pkce_verifier");
      const storedChallenge = sessionStorage.getItem("pkce_challenge");
      if (storedVerifier && storedChallenge) {
        return; // PKCE values already in storage, don't touch them
      }
    }
    await pkceManager.initialize();
  };

  useEffect(() => {
    initializePKCE();
  }, []);

  const service = useMemo(() => {
    if (!providerId) return null;
    const provider = providers[providerId];
    return provider ? OAuthService.getInstance(provider) : null;
  }, [providerId, providers]);

  useEffect(() => {
    if (!service) {
      setLoading(false);
      return;
    }

    const loadUser = async () => {
      try {
        const currentUser = await service.getUser();
        setUser(currentUser);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [service]);

  const login = useCallback(
    async (newProviderId: string) => {
      const provider = Object.values(providers).find(
        (x) => x.name == newProviderId,
      );
      if (!provider) throw new Error(`Unknown provider: ${newProviderId}`);

      const service = OAuthService.getInstance(provider);
      setProviderId(newProviderId);
      await service.login();
    },
    [providers],
  );

  const logout = useCallback(async () => {
    if (service) {
      await service.logout();
      setUser(null);
      setProviderId(null);
    }
  }, [service]);

  const isAuthenticated = useCallback(async () => {
    return service ? service.isAuthenticated() : false;
  }, [service]);

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
      isAuthenticated,
    }),
    [user, loading, error, login, logout, isAuthenticated],
  );

  return (
    <OAuthContext.Provider value={contextValue}>
      {children}
    </OAuthContext.Provider>
  );
};

// Hook
export const useOAuth = () => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error("useOAuth must be used within an OAuthProvider");
  }
  return context;
};
